'use client'

import { useEffect, useMemo, useState } from 'react'
import { Share2, Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'

function isBrowser() {
  return typeof window !== 'undefined'
}

function buildWhatsappShareUrl(text: string, url: string) {
  const msg = `${text}\n${url}`
  return `https://wa.me/?text=${encodeURIComponent(msg)}`
}

interface ArticleFeedbackProps {
  slug: string
  title?: string
  className?: string
}

export default function ArticleFeedback({
  slug,
  title = '¿Te gustó esta nota?',
  className = '',
}: ArticleFeedbackProps) {
  const newsletterKey = useMemo(
    () => `kuma_newsletter_subscribed:${slug}`,
    [slug]
  )

  const [copied, setCopied] = useState(false)
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [subscribed, setSubscribed] = useState(false)

  useEffect(() => {
    if (!isBrowser()) return
    setSubscribed(localStorage.getItem(newsletterKey) === '1')
  }, [newsletterKey])

  const currentUrl = () => (isBrowser() ? window.location.href : '')
  const shareText = () => `Te comparto esta nota de Kuma Montessori: ${title}`

  const handleCopyLink = async () => {
    if (!isBrowser()) return
    const url = currentUrl()
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      toast.success('Link copiado ✅')
      setTimeout(() => setCopied(false), 1200)
    } catch {
      try {
        const textarea = document.createElement('textarea')
        textarea.value = url
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.focus()
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
        setCopied(true)
        toast.success('Link copiado ✅')
        setTimeout(() => setCopied(false), 1200)
      } catch {
        toast.error('No pude copiar el link.')
      }
    }
  }

  const handleWhatsappShare = () => {
    if (!isBrowser()) return
    window.open(
      buildWhatsappShareUrl(shareText(), currentUrl()),
      '_blank',
      'noopener,noreferrer'
    )
  }

  const handleNativeShare = async () => {
    if (!isBrowser()) return
    const url = currentUrl()
    const canNativeShare =
      typeof navigator !== 'undefined' && 'share' in navigator

    if (!canNativeShare) {
      handleWhatsappShare()
      return
    }

    try {
      await (navigator as any).share({ title, text: shareText(), url })
      toast.success('Compartido ✅')
    } catch {
      // usuario canceló, no rompemos nada
    }
  }

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.includes('@')) {
      toast.error('Ingresá un email válido')
      return
    }
    setLoading(true)
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json()
      if (!res.ok) {
        toast.error(data.error || 'Error al suscribirse')
      } else {
        toast.success(data.message)
        setEmail('')
        setSubscribed(true)
        if (isBrowser()) localStorage.setItem(newsletterKey, '1')
      }
    } catch {
      toast.error('Error de red')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`mt-12 space-y-4 ${className}`}>
      {/* Bloque compartir */}
      <div className='rounded-2xl border border-[#EEEAE0] bg-white px-4 py-4 md:px-6 md:py-5'>
        <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
          <div className='font-allround text-base md:text-lg text-[#7D744F]'>
            {title}
          </div>

          <div className='flex items-center gap-2'>
            {/* Compartir nativo */}
            <button
              type='button'
              onClick={handleNativeShare}
              className='inline-flex items-center gap-2 rounded-full border border-[#EEEAE0] bg-white px-4 py-2 text-sm font-semibold text-[#7D744F] hover:bg-[#FAFAF7] transition'
            >
              <Share2 className='h-4 w-4' />
              Compartir
            </button>

            {/* WhatsApp */}
            <button
              type='button'
              onClick={handleWhatsappShare}
              className='inline-flex items-center gap-2 rounded-full border border-[#EEEAE0] bg-white px-3 py-2 text-sm font-semibold text-[#7D744F] hover:bg-[#FAFAF7] transition'
              title='Compartir por WhatsApp'
            >
              📲
            </button>

            {/* Copiar link */}
            <button
              type='button'
              onClick={handleCopyLink}
              className='inline-flex items-center gap-2 rounded-full border border-[#EEEAE0] bg-white px-3 py-2 text-sm font-semibold text-[#7D744F] hover:bg-[#FAFAF7] transition'
              title='Copiar link'
            >
              {copied ? (
                <Check className='h-4 w-4' />
              ) : (
                <Copy className='h-4 w-4' />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Bloque newsletter */}
      <div className='rounded-2xl border border-[#EEEAE0] bg-white px-4 py-5 md:px-6 md:py-6'>
        {subscribed ? (
          <p className='font-allround text-base text-[#00A85A] text-center'>
            ¡Ya sos parte de la Comunidad Kuma! 🌿
          </p>
        ) : (
          <div className='flex flex-col gap-4 md:flex-row md:items-center md:justify-between'>
            <div className='flex flex-col gap-1'>
              <p className='font-allround text-base md:text-lg text-[#7D744F] font-semibold'>
                ¿Querés seguir explorando?
              </p>
              <p className='text-sm text-[#9E9283]'>
                Unite a la Comunidad Kuma y recibí notas como esta.
              </p>
            </div>

            <form
              onSubmit={handleNewsletterSubmit}
              className='flex gap-2 w-full md:w-auto'
            >
              <Input
                type='email'
                placeholder='Tu email'
                className='bg-[#FAFAF7] border-[#EEEAE0] text-[#3B3B3B] placeholder:text-[#9E9283] rounded-full px-4 h-10 text-sm flex-1 md:w-52'
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
              />
              <Button
                type='submit'
                disabled={loading}
                className='bg-[#FFA474] hover:bg-[#e68d5f] text-white rounded-full px-5 h-10 text-sm font-semibold font-allround shrink-0'
              >
                {loading ? '...' : 'Unirme'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  )
}
